/**
 * Короткие звуковые отклики интерфейса.
 *
 * Звук синтезируется осциллятором Web Audio, а не берётся из файла: файл
 * пришлось бы тащить в сборку и ждать его загрузки, а синтез — это
 * несколько строк кода и ноль килобайт: наведение, нажатие, «верно»,
 * «неверно».
 *
 * Настройка «звук включён» хранится в localStorage и переживает
 * перезапуск окна: пользователь, однажды выключивший звук, не должен
 * выключать его каждый запуск. Значение читается один раз при импорте,
 * а изменения рассылаются подписчикам — так переключатель и сам сервис
 * всегда видят одно и то же состояние.
 *
 * Контекст создаётся лениво и только по требованию: браузер запрещает
 * запускать звук до первого действия пользователя, поэтому инициализация
 * при загрузке модуля привела бы к предупреждениям в консоли.
 */

import { logger } from '../lib/logger';

/** Ключ настройки в localStorage. */
const STORAGE_KEY = 'aptile.sound.enabled';

/** Подписчики на смену настройки — переключатель и любой другой UI. */
const listeners = new Set<() => void>();

/** Ленивый аудиоконтекст: один на всё окно. */
let context: AudioContext | null = null;

function readEnabled(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    // Приватный режим и подобные ограничения — тихо считаем звук включённым.
    return true;
  }
}

let enabled = readEnabled();

/** Включён ли звук сейчас. */
export function isSoundEnabled(): boolean {
  return enabled;
}

function persist(next: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
  } catch {
    // Настройка не сохранилась — это не повод ломать сессию.
  }
}

/** Меняет настройку звука и уведомляет подписчиков. */
export function setSoundEnabled(next: boolean): void {
  if (enabled === next) return;
  enabled = next;
  persist(next);
  for (const listener of listeners) listener();
}

/** Переключает звук и возвращает новое состояние. */
export function toggleSound(): boolean {
  setSoundEnabled(!enabled);
  return enabled;
}

/** Подписка на смену настройки — для `useSyncExternalStore`. */
export function subscribeSound(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Готовит контекст к воспроизведению: создаёт и выводит из паузы. */
function ensureContext(): AudioContext | null {
  try {
    if (context === null) {
      context = new AudioContext();
    }
    if (context.state === 'suspended') {
      void context.resume();
    }
    return context;
  } catch (error) {
    logger.warn('[sound] аудиоконтекст недоступен', error);
    return null;
  }
}

/** Одна нота: форма волны, частота (с глиссандо), огибающая громкости. */
interface Tone {
  readonly type: 'sine' | 'triangle';
  readonly from: number;
  readonly to?: number;
  readonly delay?: number;
  readonly duration: number;
  readonly volume: number;
}

/**
 * Проигрывает набор нот через общий фильтр: срез верхов делает звук
 * мягким, «стеклянным», а не пищащим. Если звук выключен или контекст
 * не поднялся — молча ничего не делаем.
 */
function play(tones: readonly Tone[]): void {
  if (!enabled) return;
  const audio = ensureContext();
  if (audio === null) return;
  try {
    const now = audio.currentTime;
    const filter = audio.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 4200;
    filter.connect(audio.destination);
    for (const tone of tones) {
      const start = now + (tone.delay ?? 0);
      const end = start + tone.duration;
      const gain = audio.createGain();
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(tone.volume, start + Math.min(0.012, tone.duration / 4));
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      gain.connect(filter);
      const oscillator = audio.createOscillator();
      oscillator.type = tone.type;
      oscillator.frequency.setValueAtTime(tone.from, start);
      if (tone.to !== undefined) oscillator.frequency.exponentialRampToValueAtTime(tone.to, end);
      oscillator.connect(gain);
      oscillator.start(start);
      oscillator.stop(end + 0.02);
    }
  } catch (error) {
    logger.warn('[sound] не удалось воспроизвести сигнал', error);
  }
}

/** Наведение: едва слышный «тик» — высокая короткая нота. */
export function playHover(): void {
  play([{ type: 'sine', from: 1900, to: 2200, duration: 0.045, volume: 0.025 }]);
}

/** Нажатие: мягкий «клик» — короткий спад частоты, как у тактильной кнопки. */
export function playClick(): void {
  play([
    { type: 'triangle', from: 620, to: 380, duration: 0.07, volume: 0.09 },
    { type: 'sine', from: 1240, to: 900, duration: 0.04, volume: 0.03 },
  ]);
}

/** Верно: восходящее мажорное трезвучие (до — ми — соль), светлое и короткое. */
export function playCorrect(): void {
  play([
    { type: 'sine', from: 1046.5, duration: 0.32, volume: 0.13 },
    { type: 'sine', from: 1318.5, delay: 0.07, duration: 0.34, volume: 0.12 },
    { type: 'sine', from: 1568, delay: 0.14, duration: 0.5, volume: 0.12 },
    { type: 'triangle', from: 2093, delay: 0.14, duration: 0.4, volume: 0.03 },
  ]);
}

/** Неверно: мягкий нисходящий «бум-бум» — без резкого зуммера, не обидно. */
export function playIncorrect(): void {
  play([
    { type: 'triangle', from: 330, to: 290, duration: 0.16, volume: 0.12 },
    { type: 'triangle', from: 262, to: 220, delay: 0.13, duration: 0.26, volume: 0.12 },
  ]);
}

/** Прежнее имя «динь» — тот же звук верного ответа. */
export const playDing = playCorrect;
